/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import React, { useCallback, useEffect, useState, forwardRef } from 'react'
import { Button, Checkbox, Form, Icon, Segment, Table } from 'semantic-ui-react'
import 'react-dates/initialize'
import 'react-dates/lib/css/_datepicker.css'
import { getDeploymentMetrics } from '../../services/Metrics/Metrics.service'
import { useTranslation } from 'react-i18next'
import useBreadcrumb from '../NavBar/useBreadcrumb'
import AdvancedFilters from './AdvancedFilters'
import useUser from '../Shared/UserProvider/useUser'
import { CSVLink } from 'react-csv'
import moment from 'moment'
import { getCancellationSource } from '../../services/http-client'
import { isProviderACompany } from '../../services/Provider/Provider.service'
import { TableVirtuoso } from 'react-virtuoso'


const VirtuosoSemanticOverrides = {
    Table: forwardRef((props, ref) => <Table {...props} ref={ref} celled striped selectable unstackable/>),
    TableHead: (props) => <Table.Header {...props} style={{...props.style}}/>,
    TableBody: forwardRef((props, ref) => <Table.Body {...props} ref={ref}/>),
    TableRow: (props) => <Table.Row {...props} style={{display: 'table-row'}}/>,
}


const Deployments = ({handleFiltersChange, defaultFilters}) => {
    const [t] = useTranslation()
    const {setPaths} = useBreadcrumb()
    const [loading, setLoading] = useState(false)

    const [data, setData] = useState({})
    const [activePosFilter, setActivePosFilter] = useState(false)

    const {user} = useUser()

    const loadMetrics = useCallback(async (cancellationSource) => {
        setLoading(true)
        const data = await getDeploymentMetrics({
            merchantId: defaultFilters.merchantId,
            brandIds: defaultFilters.brandIds,
            platformIds: defaultFilters.platformIds,
            activePosFilter,
        }, cancellationSource)

        if (cancellationSource?.isCancelled) return

        setData(data)
        setLoading(false)
    }, [defaultFilters.merchantId, defaultFilters.brandIds, defaultFilters.platformIds, activePosFilter])

    useEffect(() => {
        setPaths([{
            text: t('breadcrumb.home'),
            link: false,
            path: '/',
        }])
    }, [setPaths, t])

    useEffect(() => {
        const cancellationSource = getCancellationSource()
        loadMetrics(cancellationSource)

        return () => cancellationSource.cancel()
    }, [loadMetrics])

    const [localFilters, setLocalFilters] = useState({
        merchant: defaultFilters.merchant,
        brands: defaultFilters.brands,
        platforms: defaultFilters.platforms,
        dateRange: {
            startDate: moment(defaultFilters.dateRange.startDate).utc(),
            endDate: moment(defaultFilters.dateRange.endDate).utc(),
        },
        dateComparison: {
            startDate: defaultFilters.dateComparison.startDate && moment(defaultFilters.dateComparison.startDate).utc(),
            endDate: defaultFilters.dateComparison.endDate && moment(defaultFilters.dateComparison.endDate).utc(),
        },
    })

    const localHandleFiltersChange = (filters) => {
        setLocalFilters(prev => ({
            ...prev,
            ...filters,
        }))
    }

    useEffect(() => {
        handleFiltersChange(localFilters)
    }, [localFilters, handleFiltersChange])

    return (
      <>
          <div css={css`
              display: flex;
              justify-content: space-between;

              .csv-btn {
                  display: flex;
                  align-items: center;
              }
          `}>
              <AdvancedFilters
                filters={{
                    merchant: defaultFilters.merchant,
                    brands: defaultFilters.brands,
                    platforms: defaultFilters.platforms,
                }}
                onFiltersChange={localHandleFiltersChange}>
                  <Form css={css` display: flex;`}>
                      <Form.Group widths="equal">
                          {(isProviderACompany(user?.provider) || user.isLoggedInOwnScope) &&
                            <AdvancedFilters.PosFilter clearable/>}
                          <AdvancedFilters.BrandFilter/>
                          <AdvancedFilters.PlatformFilter/>
                      </Form.Group>
                      <div
                        css={css`
                            display: flex;
                            align-items: center;
                            margin-left: 1rem;
                            font-weight: bold;
                        `}
                      >
                          <Checkbox
                            toggle
                            checked={activePosFilter}
                            onChange={(e, { checked }) => {
                                setActivePosFilter(checked)
                            }}
                            label={t('deployments.active_pos')}
                          />
                      </div>
                  </Form>
              </AdvancedFilters>
              <CSVLink
                className="csv-btn"
                enclosingCharacter={'"'}
                separator=";"
                data={[['Store'].concat((data?.brands || []).flatMap((brand) => {
                    return (data?.platforms || []).map(platform => `${brand.name} - ${platform.name}`)
                }))].concat((data?.results || []).map(res => {
                    return res.map((r, i) => {
                        if (i === 0) {
                            return r
                        }
                        return r ? 'Y' : 'N'
                    })
                }))}
                filename="export-deployments.csv"
              >
                  <Button icon>
                      <Icon name="cloud download"/>
                  </Button>
              </CSVLink>
          </div>
          <Segment loading={loading} basic css={css`
              width: 100%;
              padding: 0 !important;
          `}>
              <div css={css`
                  width: 100%;
                  overflow-x: auto;
                  overflow-y: hidden;
                  min-height: 100vh;
              `}>
                  <div css={css`
                      padding-top: 20px;
                      position: relative;
                  `}>
                      <TableVirtuoso
                        data={data?.results || []}
                        useWindowScroll
                        components={VirtuosoSemanticOverrides}
                        overscan={100}
                        defaultItemHeight={80}
                        initialTopMostItemIndex={0}
                        fixedHeaderContent={() => (
                          <>
                              <Table.Row>
                                  <Table.HeaderCell/>
                                  {(data?.brands || []).map((brand, idx) => (
                                    <Table.HeaderCell key={idx} textAlign="center"
                                                      colSpan={`${(data?.platforms || []).length}`}>{brand.name}</Table.HeaderCell>
                                  ))}
                              </Table.Row>
                              <Table.Row>
                                  <Table.HeaderCell/>
                                  {(data?.brands || []).flatMap((brand, brandIdx) => {
                                      return (data?.platforms || []).map((platform, platformIdx) => (
                                        <Table.HeaderCell key={`${brandIdx}-${platformIdx}`} singleLine
                                                          textAlign="center">{platform.name}</Table.HeaderCell>
                                      ))
                                  })}
                              </Table.Row>
                          </>
                        )}
                        itemContent={(index, res) => (
                          <>
                              {res.map((r, i) => {
                                  if (i === 0) {
                                      return (
                                        <Table.Cell key={i}>{r}</Table.Cell>
                                      )
                                  }

                                  return (
                                    <Table.Cell key={i} textAlign="center">{r ? (
                                      <Icon name="check circle" color="green"/>
                                    ) : (
                                      <Icon name="times circle" color="red"/>
                                    )}</Table.Cell>
                                  )
                              })}
                          </>
                        )}
                      />
                  </div>
              </div>
          </Segment>
      </>
    )
}

export default Deployments
